import React, { useState, useEffect } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Row,
	Col,
	FormGroup,
	Input,
	Label,
} from "reactstrap";
import AddressAutocomplete from '../../../../components/AddressAutocomplete';
import { 
	validateRequiredString, 
	validateStreetAddress2,
	validateZipCode, 
	formatZipCode
} from '../../../../modules/InputsValidations';

function AddOwnerAddressModal({ 
	isOpen = false, 
	toggle = () => {}, 
	onAddressAdded = () => {}, 
	addressToEdit = null,
	availableAddressTypes = []
}) {
	const [addressData, setAddressData] = useState({
		addressType: 'PHYSICAL',
		streetAddress: '',
		streetAddress2: '',
		city: '',
		state: '',
		zipCode: '',
		country: '',
		countryId: '',
		stateId: ''
	});

	const [selectedCountry, setSelectedCountry] = useState(null);
	const [selectedState, setSelectedState] = useState(null);
	const [errors, setErrors] = useState({});
	const [zipEditable, setZipEditable] = useState(false);

	// Reset form when modal opens/closes or when editing
	useEffect(() => {
		if (isOpen) {
			if (addressToEdit) {
				// Editing existing address
				setAddressData({
					addressType: addressToEdit.addressType || 'PHYSICAL',
					streetAddress: addressToEdit.streetAddress || '',
					streetAddress2: addressToEdit.streetAddress2 || '',
					city: addressToEdit.city || '',
					state: addressToEdit.state || '',
					zipCode: addressToEdit.zipCode || '',
					country: addressToEdit.country || '',
					countryId: addressToEdit.countryId || '',
					stateId: addressToEdit.stateId || ''
				});
				setZipEditable(true);
			} else {
				// Adding new address - use first available type
				const defaultType = availableAddressTypes.length > 0 ? availableAddressTypes[0] : '';
				setAddressData({
					addressType: defaultType,
					streetAddress: '',
					streetAddress2: '',
					city: '',
					state: '',
					zipCode: '',
					country: '',
					countryId: '',
					stateId: ''
				});
				setZipEditable(false);
			}
			setSelectedCountry(null);
			setSelectedState(null);
			setErrors({});
		}
	}, [isOpen, addressToEdit, availableAddressTypes]);

	// Handle input changes
	const handleInputChange = (field, value) => {
		let processedValue = value;
		
		// Apply formatting for specific fields
		switch (field) {
			case 'zipCode':
				processedValue = formatZipCode(value);
				break;
			default:
				break;
		}
		
		setAddressData(prev => ({
			...prev,
			[field]: processedValue
		}));
		
		// Real-time validation
		let validationResult = { isValid: true, message: '' };
		
		switch (field) {
			case 'addressType':
				validationResult = validateRequiredString(value, 255, 'Address Type');
				break;
			case 'streetAddress':
				validationResult = validateRequiredString(value, 255, 'Street Address');
				break;
			case 'streetAddress2':
				validationResult = validateStreetAddress2(value);
				break;
			case 'zipCode':
				validationResult = validateZipCode(processedValue);
				break;
			case 'city':
				validationResult = validateRequiredString(value, 255, 'City');
				break;
			case 'state':
				validationResult = validateRequiredString(value, 255, 'State');
				break;
			case 'country':
				validationResult = validateRequiredString(value, 255, 'Country');
				break;
			default:
				break;
		}
		
		if (!validationResult.isValid) {
			setErrors(prev => ({
				...prev,
				[field]: validationResult.message
			}));
		} else {
			setErrors(prev => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};

	// Nueva lógica: manejar cambios en Street Address con AddressAutocomplete
	const handleStreetChange = (text) => {
		setAddressData(prev => ({
			...prev,
			streetAddress: text,
			city: '',
			state: '',
			zipCode: '',
			country: '',
			countryId: '',
			stateId: ''
		}));
		setZipEditable(false);
		setErrors(prev => {
			const copy = { ...prev };
			delete copy.city;
			delete copy.zipCode;
			delete copy.streetAddress;
			delete copy.state;
			delete copy.country;
			return copy;
		});
	};

	const handleStreetSelect = (item) => {
		const label = item?.label || '';
		const details = item?.details || {};
		const city = details.city || '';
		const state = details.state || '';
		const country = details.country || '';
		const zipCode = details.zipCode || '';

		setAddressData(prev => ({
			...prev,
			streetAddress: label,
			city,
			state,
			country,
			zipCode,
			countryId: '',
			stateId: ''
		}));
		setZipEditable(!zipCode);

		// Validaciones al autocompletar
		const nextErrors = { ...errors };
		if (!label.trim()) {
			nextErrors.streetAddress = 'Street Address is required';
		} else {
			delete nextErrors.streetAddress;
		}
		if (!city.trim()) {
			nextErrors.city = 'City is required';
		} else {
			delete nextErrors.city;
		}
		if (!state.trim()) {
			nextErrors.state = 'State is required';
		} else {
			delete nextErrors.state;
		}
		if (!country.trim()) {
			nextErrors.country = 'Country is required';
		} else {
			delete nextErrors.country;
		}
		if (!zipCode) {
			nextErrors.zipCode = 'Zip Code is required';
		} else if (!validateZipCode(zipCode).isValid) {
			nextErrors.zipCode = validateZipCode(zipCode).message;
		} else {
			delete nextErrors.zipCode;
		}
		setErrors(nextErrors);
	};

	// Handle country selection
	const handleCountrySelect = (country) => {
		if (!country) return;
		
		setSelectedCountry(country);
		setAddressData(prev => ({
			...prev,
			country: country.name || country.label || country,
			countryId: country.placeId || country.id || country.value || '',
			state: '',
			stateId: '',
			city: ''
		}));
		setSelectedState(null);
		
		// Clear errors for dependent fields
		setErrors(prev => {
			const newErrors = { ...prev };
			delete newErrors.state;
			delete newErrors.city;
			return newErrors;
		});
	};

	// Handle state selection
	const handleStateSelect = (state) => {
		if (!state) return;
		
		setSelectedState(state);
		setAddressData(prev => ({
			...prev,
			state: state.name || state.label || state,
			stateId: state.placeId || state.id || state.value || '',
			city: ''
		}));
		
		// Clear city error
		setErrors(prev => {
			const newErrors = { ...prev };
			delete newErrors.city;
			return newErrors;
		});
	};

	// Handle city selection
	const handleCitySelect = (city) => {
		if (!city) return;
		
		setAddressData(prev => ({
			...prev,
			city: city.name || city.label || city,
			cityId: city.placeId || city.id || city.value || ''
		}));
		
		// Clear city error
		setErrors(prev => {
			const newErrors = { ...prev };
			delete newErrors.city;
			return newErrors;
		});
	};

	// Check if form is complete
	const isFormComplete = () => {
		const requiredFields = ['addressType', 'streetAddress', 'city', 'state', 'country', 'zipCode'];
		const allFilled = requiredFields.every(field => {
			const value = addressData[field];
			return value && String(value).trim() !== '';
		});
		const zipOk = validateZipCode(addressData.zipCode).isValid;
		return allFilled && zipOk && Object.keys(errors).length === 0;
	};

	// Handle save
	const handleSave = () => {
		if (!isFormComplete()) {
			// Set errors for missing required fields
			const newErrors = {};
			const req = ['addressType', 'streetAddress', 'city', 'state', 'country', 'zipCode'];
			req.forEach((f) => {
				if (!addressData[f] || String(addressData[f]).trim() === '') {
					newErrors[f] = `${f.replace(/^[a-z]/, (m) => m.toUpperCase())} is required`;
				}
			});
			if (addressData.zipCode && !validateZipCode(addressData.zipCode).isValid) {
				newErrors.zipCode = validateZipCode(addressData.zipCode).message;
			}
			setErrors(newErrors);
			return;
		}

		// Call the callback with the address data
		onAddressAdded(addressData, addressToEdit);
		toggle();
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="lg" className='modal-dialog-centered'>
			<ModalHeader toggle={toggle}>
				{addressToEdit ? 'Edit Owner Address' : 'Add Owner Address'}
			</ModalHeader>
			<ModalBody className='py-0'>
				<Row>
					<Col md="12">
						<FormGroup className="mb-2">
							<Label for="addressType" className="mb-1">
								Address Type <span>*</span>
							</Label>
							<Input
								id="addressType"
								type="select"
								value={addressData.addressType}
								onChange={(e) => handleInputChange('addressType', e.target.value)}
								className={errors.addressType ? 'is-invalid' : ''}
								bsSize="sm">
								<option value="">Select address type</option>
								{availableAddressTypes.map(type => (
									<option key={type} value={type}>{type}</option>
								))}
							</Input>
							{errors.addressType && (
								<div className="invalid-feedback d-block">{errors.addressType}</div>
							)}
						</FormGroup>
					</Col>
				</Row>

				<Row>
					<Col md="12">
						<FormGroup className="mb-2">
							<Label for="streetAddress" className="mb-1">
								Street Address 1 <span>*</span>
							</Label>
							<AddressAutocomplete
								value={addressData.streetAddress}
								onChange={handleStreetChange}
								onSelect={handleStreetSelect}
								placeholder="Start typing address..."
								className={errors.streetAddress ? 'is-invalid' : ''}
								bsSize="sm"
							/>
							{errors.streetAddress && (
								<div className="invalid-feedback d-block">{errors.streetAddress}</div>
							)}
						</FormGroup>
					</Col>
				</Row>

				<Row>
					<Col md="12">
						<FormGroup className="mb-2">
							<Label for="streetAddress2" className="mb-1">
								Street Address 2
							</Label>
							<Input
								id="streetAddress2"
								type="text"
								value={addressData.streetAddress2}
								onChange={(e) => handleInputChange('streetAddress2', e.target.value)}
								placeholder="Apartment, suite, etc. (optional)"
								className={errors.streetAddress2 ? 'is-invalid' : ''}
								bsSize="sm"
								maxLength={255}
							/>
							{errors.streetAddress2 && (
								<div className="invalid-feedback d-block">{errors.streetAddress2}</div>
							)}
						</FormGroup>
					</Col>
				</Row>

				<Row>
					<Col md="6">
						<FormGroup className="mb-2">
							<Label for="city" className="mb-1">
								City <span>*</span>
							</Label>
							<Input
								id="city"
								type="text"
								value={addressData.city}
								readOnly
								disabled
								placeholder="Enter city"
								className={`${errors.city ? 'is-invalid' : ''}`}
								bsSize="sm"
							/>
							{errors.city && (
								<div className="invalid-feedback d-block">{errors.city}</div>
							)}
						</FormGroup>
					</Col>

					<Col md="6">
						<FormGroup className="mb-2">
							<Label for="state" className="mb-1">
								State <span>*</span>
							</Label>
							<Input
								id="state"
								type="text"
								value={addressData.state}
								readOnly
								disabled
								placeholder="Enter state"
								className={`${errors.state ? 'is-invalid' : ''}`}
								bsSize="sm"
							/>
							{errors.state && (
								<div className="invalid-feedback d-block">{errors.state}</div>
							)}
						</FormGroup>
					</Col>
				</Row>

				<Row>
					<Col md="6">
						<FormGroup className="mb-2">
							<Label for="zipCode" className="mb-1">
								Zip Code <span>*</span>
							</Label>
							<Input
								id="zipCode"
								type="text"
								value={addressData.zipCode}
								readOnly={!zipEditable}
								disabled={!zipEditable}
								placeholder="Enter zip code"
								maxLength={5}
								className={`${errors.zipCode ? 'is-invalid' : ''}`}
								onChange={(e) => handleInputChange('zipCode', e.target.value)}
								bsSize="sm"
							/>
							{errors.zipCode && (
								<div className="invalid-feedback d-block">{errors.zipCode}</div>
							)}
						</FormGroup>
					</Col>

					<Col md="6">
						<FormGroup className="mb-2">
							<Label for="country" className="mb-1">
								Country <span>*</span>
							</Label>
							<Input
								id="country"
								type="text"
								value={addressData.country}
								readOnly
								disabled
								placeholder="Enter country"
								className={`${errors.country ? 'is-invalid' : ''}`}
								bsSize="sm"
							/>
							{errors.country && (
								<div className="invalid-feedback d-block">{errors.country}</div>
							)}
						</FormGroup>
					</Col>
				</Row>
			</ModalBody>
			<ModalFooter>
				<div>
					<Button className='border' color="secondary" onClick={toggle}>
						Cancel
					</Button>
					<Button className='border' color="primary" onClick={handleSave} disabled={!isFormComplete()}>
						{addressToEdit ? 'Update' : 'Add'} address
					</Button>
				</div>
			</ModalFooter>
		</Modal>
	);
}

export default AddOwnerAddressModal;


